/**
 * Pure helpers and constants of the /me self-service web portal (#1768).
 *
 * No React here. The three activity-marker helpers below are the ONLY place in
 * the portal that touches web storage (enforced by mePortal.privacy.test.tsx).
 */
import type { AccessContextValue } from '@/contexts/AccessContext';
import type { Module } from '@/lib/modules';
import { addDays } from 'date-fns';

import { formatLocalDate, parseDateString, toOrgDateOnly } from '@/utils/dateUtils';

import { ME_BASE_PATH } from './mePath';

export { ME_BASE_PATH, isMePath, meSentryReplayRates } from './mePath';

/**
 * Modules that open the portal. The exact set of the backend guard on every
 * `/api/v1/employee/me/*` route (`employeeSelfServiceGuard`, cmd/api/main.go:1693):
 * a caller without one of them only gets 403s there.
 */
export const ME_PORTAL_MODULES = [
  'self-service',
  'mobile-employee',
  'time-tracking-workforce',
] as const satisfies readonly Module[];

type PortalAccess = Pick<AccessContextValue, 'isAdmin' | 'hasModule'>;

/** May the caller use the portal (UX gate; the backend guard is the boundary)? */
export function canUseMePortal(access: PortalAccess): boolean {
  return access.isAdmin || ME_PORTAL_MODULES.some((module) => access.hasModule(module));
}

/** An app-only employee (no web module) lands on the portal instead of the lock-out. */
export function landsOnMePortal(access: PortalAccess & Pick<AccessContextValue, 'webAccess'>): boolean {
  return (
    !access.isAdmin &&
    !access.webAccess &&
    (access.hasModule('self-service') || access.hasModule('mobile-employee'))
  );
}

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F]/;

/**
 * The same-origin relative path a `redirect_url` names, or null.
 *
 * Accepts only a path that starts with exactly one `/` (no `//`, no `/\`), has
 * no backslash and no control character, and resolves to this origin. A path
 * under `/sign-in` is rejected so sign-in never loops onto itself.
 */
export function safeRedirectPath(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw === '') return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  if (raw.includes('\\') || CONTROL_CHARS.test(raw)) return null;
  let parsed: URL;
  try {
    parsed = new URL(raw, window.location.origin);
  } catch {
    return null;
  }
  if (parsed.origin !== window.location.origin) return null;
  if (parsed.pathname === '/sign-in' || parsed.pathname.startsWith('/sign-in/')) return null;
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

/** The sign-in URL that carries the signed-out deep link through sign-in. */
export function buildSignInRedirect(pathname: string, search: string): string {
  return `/sign-in?redirect_url=${encodeURIComponent(pathname + search)}`;
}

// --- Session (idle sign-out) -------------------------------------------------

/** localStorage key of the last-activity timestamp (epoch ms, digits only). */
export const ME_LAST_ACTIVITY_KEY = 'me-last-activity';
export const ME_IDLE_TIMEOUT_MS = 30 * 60 * 1000;
export const ME_IDLE_CHECK_INTERVAL_MS = 30_000;
export const ME_ACTIVITY_WRITE_THROTTLE_MS = 10_000;

/**
 * The stored last-activity timestamp; a non-numeric value is removed and reads
 * as null. A value in the future (the clock moved back) is rewritten as now,
 * so it expires 30 minutes later instead of never.
 */
export function readLastActivity(storage: Storage = window.localStorage): number | null {
  const raw = storage.getItem(ME_LAST_ACTIVITY_KEY);
  if (raw === null) return null;
  const value = /^\d+$/.test(raw) ? Number(raw) : Number.NaN;
  if (!Number.isFinite(value)) {
    storage.removeItem(ME_LAST_ACTIVITY_KEY);
    return null;
  }
  const now = Date.now();
  if (value > now) {
    writeLastActivity(now, storage);
    return now;
  }
  return value;
}

export function writeLastActivity(now: number, storage: Storage = window.localStorage): void {
  storage.setItem(ME_LAST_ACTIVITY_KEY, String(Math.floor(now)));
}

export function clearLastActivity(storage: Storage = window.localStorage): void {
  storage.removeItem(ME_LAST_ACTIVITY_KEY);
}

export function isMeSessionExpired(lastActivity: number | null, now: number): boolean {
  return lastActivity !== null && now - lastActivity >= ME_IDLE_TIMEOUT_MS;
}

/**
 * Today in the org zone. The portal does not load the org timezone (that call
 * is outside its allowlist), so this is the documented Europe/Zurich default.
 */
export function orgToday(): string {
  return toOrgDateOnly(new Date().toISOString());
}

/** `YYYY-MM-DD` plus `days` calendar days (date-only; never through `toISOString()`). */
export function addDaysToDate(date: string, days: number): string {
  const parsed = parseDateString(date);
  return parsed ? formatLocalDate(addDays(parsed, days)) : '';
}

/** The portal URL handed over in the login panel. */
export function mePortalUrl(origin: string = window.location.origin): string {
  return `${origin}${ME_BASE_PATH}`;
}
